import { createEntityAdapter, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';

import { contractSetupAllStateClear } from '@app/store/contract-setup/actions';
import Host from '@app/store/Host';
import LocaleCode from '@app/store/LocaleCode';

import { HostStoreError } from './errors';

export const hostsAdapter = createEntityAdapter<Host>({
  selectId: (host) => host.dataLabel,
});

/**
 * @see https://github.com/modusign/modusign-client/pull/7333#issuecomment-1271047995
 * @prop defaultHostDataLabel 대면서명자에게 호스트가 설정되어있지 않으면 defaultHost를 이용한다.
 */
export type HostState = EntityState<Host> & {
  defaultHostDataLabel: null | string;
  error: null | HostStoreError;
};

const initialState: HostState = hostsAdapter.getInitialState({
  defaultHostDataLabel: null,
  error: null,
});

export const {
  selectById: selectHost,
  selectEntities: selectHosts,
  selectIds: selectHostDataLabels,
} = hostsAdapter.getSelectors();

export const hostSlice = createSlice({
  name: 'hostSlice',
  initialState,
  reducers: {
    initializeDefaultHost: (
      state,
      {
        payload: { defaultHost },
      }: PayloadAction<{
        defaultHost: Host;
      }>
    ) => {
      const { dataLabel } = defaultHost;
      state.error = null;
      state.defaultHostDataLabel = dataLabel;
      hostsAdapter.setAll(state, [defaultHost]);
    },
    loadHosts: (
      state,
      {
        payload: { hosts },
      }: PayloadAction<{
        hosts: Array<{ dataLabel: string; name: string; contactInfo: string; locale: LocaleCode }>;
      }>
    ) => {
      const defaultHost = selectHost(state, state.defaultHostDataLabel || '');

      if (!defaultHost) {
        state.error = 'HOST_STORE_NOT_INITIALIZED';
        return;
      }

      hostsAdapter.setAll(state, [defaultHost, ...hosts]);
    },
  },
  extraReducers: (builder) =>
    builder.addCase(contractSetupAllStateClear, () => {
      return initialState;
    }),
});

export const { initializeDefaultHost, loadHosts } = hostSlice.actions;
export default hostSlice.reducer;
